/**
 * Sign-In Dialog Context
 *
 * Manages the sign-in dialog state separately from auth state.
 * This allows the dialog to be opened/closed from anywhere in the app
 * while keeping form state isolated.
 */

import React, { createContext, useContext, useState, useCallback, useRef } from "react";

// ============================================================================
// Types
// ============================================================================

export type DialogView =
  | "main"
  | "signin-password"
  | "signup-password"
  | "reset-password"
  | "account-not-found"
  // Mirrors SignInFormView's one-time-code steps (email_code_auth experiment).
  | "email-code-verify"
  | "email-code-name";

export interface SignInOptions {
  asSignUp?: boolean;
  onSuccess?: () => void;
  /**
   * Where a redirect-based sign-in should land, as an app path.
   *
   * `onSuccess` is a closure, and the redirect arm destroys the page holding
   * it, so a caller that navigates somewhere on success has to say where in a
   * form that survives a page load. Set it to the same destination `onSuccess`
   * navigates to; leave it off when success means "stay here and re-render
   * signed in", which is what the redirect already does by itself.
   */
  returnTo?: string;
  preventClose?: boolean;
  /**
   * Skip the welcome view and open straight to a specific view. Used by the
   * paginated quiz when the user has already tapped "Continue with email" on
   * the inline auth row — no need to show them the welcome buttons again.
   */
  initialView?: DialogView;
  /**
   * Optional override for the dialog title — lets a flow drop in copy that
   * matches its own context ("Reserve your seat") instead of the generic
   * "Welcome to Outclose". Falls back to the i18n strings when not provided.
   */
  title?: string;
  /** Optional subtitle override — same intent as `title`. */
  subtitle?: string;
}

export interface SignInDialogState {
  // Dialog visibility
  isOpen: boolean;

  // Current view
  view: DialogView;

  // Form fields
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;

  // Validation errors (local, not from auth)
  emailError: string | null;
  passwordError: string | null;
  confirmPasswordError: string | null;
  nameError: string | null;

  // Password reset
  resetEmailSent: boolean;

  // Options passed when opening
  options: SignInOptions;
}

export interface SignInDialogActions {
  // Dialog control
  open: (options?: SignInOptions) => void;
  close: () => void;
  forceClose: () => void;

  // View navigation
  setView: (view: DialogView) => void;
  goBack: () => void;

  // Form field setters
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
  setConfirmPassword: (confirmPassword: string) => void;
  setFirstName: (firstName: string) => void;
  setLastName: (lastName: string) => void;

  // Error setters
  setEmailError: (error: string | null) => void;
  setPasswordError: (error: string | null) => void;
  setConfirmPasswordError: (error: string | null) => void;
  setNameError: (error: string | null) => void;
  clearAllErrors: () => void;

  // Password reset
  setResetEmailSent: (sent: boolean) => void;

  // Reset entire form
  resetForm: () => void;
}

export interface SignInDialogContextValue extends SignInDialogState, SignInDialogActions {}

// ============================================================================
// Context
// ============================================================================

export const SignInDialogContext = createContext<SignInDialogContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================
interface SignInDialogProviderProps {
  children: React.ReactNode;
}

export function SignInDialogProvider({ children }: SignInDialogProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setViewState] = useState<DialogView>("main");
  const [email, setEmailState] = useState("");
  const [password, setPasswordState] = useState("");
  const [confirmPassword, setConfirmPasswordState] = useState("");
  const [firstName, setFirstNameState] = useState("");
  const [lastName, setLastNameState] = useState("");
  const [emailError, setEmailErrorState] = useState<string | null>(null);
  const [passwordError, setPasswordErrorState] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordErrorState] = useState<string | null>(null);
  const [nameError, setNameErrorState] = useState<string | null>(null);
  const [resetEmailSent, setResetEmailSentState] = useState(false);
  const optionsRef = useRef<SignInOptions>({});

  const clearAllErrors = useCallback(() => {
    setEmailErrorState(null);
    setPasswordErrorState(null);
    setConfirmPasswordErrorState(null);
    setNameErrorState(null);
  }, []);

  const resetForm = useCallback(() => {
    setViewState("main");
    setEmailState("");
    setPasswordState("");
    setConfirmPasswordState("");
    setFirstNameState("");
    setLastNameState("");
    setResetEmailSentState(false);
    clearAllErrors();
  }, [clearAllErrors]);

  const open = useCallback((options?: SignInOptions) => {
    optionsRef.current = options ?? {};
    // Only reset view when not already open (avoid resetting mid-form).
    // Honor initialView when provided (e.g., a flow wants to land straight on
    // the email signup form, skipping the welcome OAuth buttons).
    if (!isOpen) {
      setViewState(options?.initialView ?? "main");
    }
    setIsOpen(true);
  }, [isOpen]);

  const forceClose = useCallback(() => {
    setIsOpen(false);
    resetForm();
    // Options must not outlive the dialog that was opened with them. They
    // carry `onSuccess`, which some callers use to navigate ("log in" on the
    // onboarding intro sends you to the app), and the auth listener reads
    // them on any sign-in. Left in place, a dialog opened and dismissed
    // earlier would redirect a sign-in performed later somewhere else.
    optionsRef.current = {};
  }, [resetForm]);

  const close = useCallback(() => {
    if (optionsRef.current.preventClose) return;
    forceClose();
  }, [forceClose]);

  const setView = useCallback((newView: DialogView) => {
    setViewState(newView);
    clearAllErrors();
  }, [clearAllErrors]);

  const goBack = useCallback(() => {
    if (view === "reset-password") {
      setViewState("signin-password");
      setResetEmailSentState(false);
    } else if (
      view === "signin-password" ||
      view === "signup-password" ||
      view === "email-code-verify"
    ) {
      setViewState("main");
      setPasswordState("");
      setConfirmPasswordState("");
      setFirstNameState("");
      setLastNameState("");
    } else {
      setEmailState("");
    }
    clearAllErrors();
  }, [view, clearAllErrors]);

  const setEmail = useCallback((value: string) => {
    setEmailState(value);
    setEmailErrorState(null);
  }, []);

  const setPassword = useCallback((value: string) => {
    setPasswordState(value);
    setPasswordErrorState(null);
  }, []);

  const setConfirmPassword = useCallback((value: string) => {
    setConfirmPasswordState(value);
    setConfirmPasswordErrorState(null);
  }, []);

  const setFirstName = useCallback((value: string) => {
    setFirstNameState(value);
    setNameErrorState(null);
  }, []);

  const setLastName = useCallback((value: string) => {
    setLastNameState(value);
  }, []);

  const value: SignInDialogContextValue = {
    // State
    isOpen,
    view,
    email,
    password,
    confirmPassword,
    firstName,
    lastName,
    emailError,
    passwordError,
    confirmPasswordError,
    nameError,
    resetEmailSent,
    options: optionsRef.current,

    // Actions
    open,
    close,
    forceClose,
    setView,
    goBack,
    setEmail,
    setPassword,
    setConfirmPassword,
    setFirstName,
    setLastName,
    setEmailError: setEmailErrorState,
    setPasswordError: setPasswordErrorState,
    setConfirmPasswordError: setConfirmPasswordErrorState,
    setNameError: setNameErrorState,
    clearAllErrors,
    setResetEmailSent: setResetEmailSentState,
    resetForm,
  };

  return (
    <SignInDialogContext.Provider value={value}>
      {children}
    </SignInDialogContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useSignInDialog(): SignInDialogContextValue {
  const context = useContext(SignInDialogContext);
  if (!context) {
    throw new Error("useSignInDialog must be used within a SignInDialogProvider");
  }
  return context;
}
